import {
  format,
  formatDistanceToNow,
  isToday,
  isYesterday,
  differenceInCalendarDays,
  parseISO,
  addHours,
} from 'date-fns';
import { getTimeZoneInfo } from '@/utils/timeZoneUtils';
import { simpleDebugLog } from '@/utils/simpleDebugLogger';

export function useTimestampFormat() {
  function formatRelative(timestamp: string | Date): string {
    const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInMinutes < 60 * 24) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else if (differenceInCalendarDays(now, date) < 7) {
      const days = differenceInCalendarDays(now, date);
      return `${days} day${days === 1 ? '' : 's'} ago`;
    } else {
      return format(date, 'MMM d');
    }
  }

  function formatAbsoluteFull(timestamp: string | Date): string {
    const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
    return format(date, 'EEEE, MMMM d, yyyy');
  }

  function formatAbsoluteAbbreviated(timestamp: string | Date): string {
    const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
    return format(date, 'MMM d, yyyy');
  }

  function formatAbsoluteNumeric(timestamp: string | Date): string {
    const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
    return format(date, 'MM/dd/yy');
  }

  function formatRelativeOrAbsoluteDate(input: string | Date): string {
    const date = new Date(input);
    const now = new Date();

    const msInDay = 1000 * 60 * 60 * 24;
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / msInDay);

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return format(date, 'EEEE'); // i.e. "Monday"
    if (diffInDays <= 365) return format(date, 'EEEE, MMMM d'); // i.e. "Monday, April 14"

    return format(date, 'EEEE, MMMM d, yyyy'); // i.e. "Monday, April 13, 2024"
  }

  function formatTime(timestamp: string | Date): string {
    const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
    return format(date, 'h:mm a').toLowerCase();
  }

  /**
   * Format the current time in a specific time zone
   * @param timeZone Time zone in format 'UTC+X' or 'UTC-X' (e.g., 'UTC-7', 'UTC+1')
   * @param currentTime Optional Date object representing the current time (defaults to now)
   * @returns Formatted time string in 12-hour format with timezone abbreviation
   */
  function formatLocalTime(timeZone = 'UTC+0', currentTime?: Date): string {
    try {
      const safeTimeZone = timeZone || 'UTC+0';
      const utcNow = currentTime || new Date();

      let offsetHours = 0;

      // Extract offset from timezone string (e.g., "UTC+5:30" => +5.5 hours)
      if (safeTimeZone.startsWith('UTC')) {
        try {
          const offsetMatch = safeTimeZone.match(/UTC([+-])(\d+)(?::(\d+))?/);
          if (offsetMatch) {
            const sign = offsetMatch[1] === '+' ? 1 : -1;
            const hours = parseInt(offsetMatch[2], 10) || 0;
            const minutes = parseInt(offsetMatch[3], 10) || 0;
            offsetHours = sign * (hours + minutes / 60);
          }
        } catch (e) {
          simpleDebugLog.warn('Error parsing time zone offset:', e);
        }
      }

      // Calculate the local time by adjusting the UTC time with the offset
      const localDate = new Date(utcNow.getTime());
      localDate.setUTCHours(localDate.getUTCHours() + offsetHours);

      // Get time zone abbreviation
      let tzAbbreviation = 'UTC';
      try {
        const tzInfo = getTimeZoneInfo(safeTimeZone);
        tzAbbreviation = tzInfo.abbreviation;
      } catch (e) {
        simpleDebugLog.warn('Error getting time zone info:', e);
      }

      // Format the time in 12-hour format with timezone abbreviation
      const hours24 = localDate.getUTCHours();
      const minutes = localDate.getUTCMinutes();

      // Convert to 12-hour format
      const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
      const ampm = hours24 >= 12 ? 'pm' : 'am';
      const minutesStr = minutes.toString().padStart(2, '0');

      const formattedTime = `${hours12}:${minutesStr} ${ampm}`;
      return `${formattedTime} (${tzAbbreviation})`;
    } catch (error) {
      simpleDebugLog.error('Error in formatLocalTime:', error);
      // Fallback to a simple format
      const now = currentTime || new Date();
      return `${now.getHours()}:${now.getMinutes().toString().padStart(2, '0')} (local time)`;
    }
  }

  /**
   * Format the last active time in a user-friendly way
   * @param timestamp The timestamp to format
   * @returns Formatted string (e.g., 'Active just now', 'Active 5 min ago', 'Active 2 hours ago')
   */
  function formatLastActive(timestamp: string | Date): string {
    const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Active just now';
    } else if (diffInMinutes < 60) {
      return `Active ${diffInMinutes} min ago`;
    } else if (diffInMinutes < 60 * 24) {
      const hours = Math.floor(diffInMinutes / 60);
      return `Active ${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (isYesterday(date)) {
      return 'Active yesterday';
    } else {
      const days = differenceInCalendarDays(now, date);
      return `Active ${days} day${days === 1 ? '' : 's'} ago`;
    }
  }

  return {
    formatRelative,
    formatAbsoluteFull,
    formatLocalTime,
    formatLastActive,
    formatAbsoluteAbbreviated,
    formatAbsoluteNumeric,
    formatRelativeOrAbsoluteDate,
    formatTime,
  };
}
